import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import { API_BASE_URL } from "../config/apiEnv";
import { refreshToken } from "../api/endpoints/auth"; 

/* =========================
   Axios Instance
========================= */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: new AxiosHeaders({ "Content-Type": "application/json" }),
  withCredentials: true, // For cookies
});

/* =========================
   Request Interceptor
========================= */

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = sessionStorage.getItem("accessToken");
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }
  if (token) {
    (config.headers as AxiosHeaders).setAuthorization(`Bearer ${token}`);
  }

  const selectedCompany = JSON.parse(sessionStorage.getItem("selectedCompany") || "{}");
  const companyId = selectedCompany?.id;
  if (companyId) {
    config.params = {
      ...config.params,
      companyId,
    };
  }

  return config;
});

/* =========================
   Refresh Token Logic
========================= */
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

/* =========================
   Response Interceptor
========================= */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError & { config?: any }) => {
    const originalRequest = error.config;

    if (!error.response || !originalRequest) return Promise.reject(error);

    // Do not perform token refresh during login or refresh-token itself
if (
  originalRequest.url?.includes("auth/login") ||
  originalRequest.url?.includes("auth/refresh-token")
) {
  return Promise.reject(error);
}

    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
       // Backend retrieves the refresh token from cookies and issues a new access token
        const newToken = await refreshToken();

        sessionStorage.setItem("accessToken", newToken); //update token
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        sessionStorage.clear();
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
